import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Table, Input, Button, message, Spin, Empty } from 'antd';
import dayjs from 'dayjs';
import { makePostCall, getUserDetails } from '../utils/helper';

interface SplitStudyModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
  /** The PACS / Worklist table row (a pacs_studies record with nested pacs_order). */
  record: any;
}

interface SeriesRow {
  seriesUID: string;
  seriesNumber: number;
  seriesDescription: string;
  modality: string;
  bodyPart: string;
  numInstances: number;
}

/**
 * Resolve a DICOMweb config URL (QIDO / WADO) the same way the PACS page does,
 * falling back to the dev endpoint when no runtime config is present.
 */
const getConfigUrl = (urlType: 'qidoRoot' | 'wadoUriRoot') => {
  const config = (window as any).config;
  const dicomwebSource = config?.dataSources?.find(
    (source: any) => source.sourceName === 'dicomweb'
  );
  return (
    dicomwebSource?.configuration?.[urlType] ||
    `https://dev-api.photonpacs.com/dicom/aets/DCM4CHEE/${urlType === 'wadoUriRoot' ? 'wado' : 'rs'}`
  );
};

/**
 * Split Study dialog.
 *
 * Lists every series in the study (via QIDO-RS) and lets the user select a subset and
 * supply a NEW accession number. Submitting creates an additional order for the same
 * patient + StudyInstanceUID (backend: POST /split-study).
 */
const SplitStudyModal: React.FC<SplitStudyModalProps> = ({ visible, onCancel, onSuccess, record }) => {
  const [seriesList, setSeriesList] = useState<SeriesRow[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newAccNo, setNewAccNo] = useState('');

  const order = record?.pacs_order || {};
  const patient = order?.patient || {};
  const studyUID = record?.ps_study_uid;
  const sourceOrderId = order?.pacs_ord_id;

  const patientHeader = useMemo(
    () => ({
      name: patient?.pat_name || '-',
      id: patient?.pat_pin || '-',
      sex: patient?.pat_sex || '-',
      accession: order?.po_acc_no || '-',
      scanDateTime: record?.ps_study_dt_tm
        ? dayjs(record.ps_study_dt_tm).format('YYYY-MM-DD / HH:mm:ss')
        : '-',
    }),
    [record]
  );

  // Fetch series whenever the dialog opens for a study.
  useEffect(() => {
    if (!visible || !studyUID) return;

    let cancelled = false;
    const fetchSeries = async () => {
      setLoading(true);
      setSeriesList([]);
      setSelectedKeys([]);
      setNewAccNo('');
      try {
        const qidoBase = getConfigUrl('qidoRoot');
        const response = await fetch(`${qidoBase}/studies/${studyUID}/series`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const seriesData = await response.json();

        const parsed: SeriesRow[] = (seriesData || [])
          .map((series: any) => ({
            seriesUID: series['0020000E']?.Value?.[0],
            seriesDescription: series['0008103E']?.Value?.[0] || 'Unknown Series',
            modality: series['00080060']?.Value?.[0] || 'OT',
            seriesNumber: parseInt(series['00200011']?.Value?.[0]) || 0,
            bodyPart: series['00180015']?.Value?.[0] || '',
            numInstances: parseInt(series['00201209']?.Value?.[0]) || 0,
          }))
          .filter((s: SeriesRow) => s.seriesUID)
          .sort((a: SeriesRow, b: SeriesRow) => a.seriesNumber - b.seriesNumber);

        if (!cancelled) {
          setSeriesList(parsed);
          // Default: nothing selected — the user explicitly chooses which series to split.
          setSelectedKeys([]);
        }
      } catch (error: any) {
        if (!cancelled) message.error(`Failed to fetch series: ${error.message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSeries();
    return () => {
      cancelled = true;
    };
  }, [visible, studyUID]);

  const handleSubmit = async () => {
    if (!sourceOrderId) {
      message.error('Source order id not found for this study');
      return;
    }
    if (!newAccNo.trim()) {
      message.warning('Please enter a new accession number');
      return;
    }
    if (selectedKeys.length === 0) {
      message.warning('Please select at least one series to split');
      return;
    }
    if (newAccNo.trim() === order?.po_acc_no) {
      message.warning('The new accession number must be different from the original');
      return;
    }

    const selectedSeries = seriesList.filter(s => selectedKeys.includes(s.seriesUID));

    setSubmitting(true);
    try {
      const response = await makePostCall('/split-study', {
        source_order_id: sourceOrderId,
        new_accession_no: newAccNo.trim(),
        selected_series: selectedSeries,
        user_id: getUserDetails()?.username,
      });

      if (response.data?.success) {
        message.success('Study split successfully');
        onSuccess?.();
        onCancel();
      } else {
        message.error(response.data?.message || 'Failed to split study');
      }
    } catch (error: any) {
      // 409 = accession already mapped to another study (handled by the backend).
      message.error(error?.response?.data?.message || 'Failed to split study. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: 'Series No',
      dataIndex: 'seriesNumber',
      key: 'seriesNumber',
      width: 90,
    },
    {
      title: 'Description',
      dataIndex: 'seriesDescription',
      key: 'seriesDescription',
      ellipsis: true,
    },
    {
      title: 'Modality',
      dataIndex: 'modality',
      key: 'modality',
      width: 90,
    },
    {
      title: 'Body Part',
      dataIndex: 'bodyPart',
      key: 'bodyPart',
      width: 110,
      render: (val: string) => val || '-',
    },
    {
      title: 'Images',
      dataIndex: 'numInstances',
      key: 'numInstances',
      width: 80,
      align: 'right' as const,
    },
  ];

  const headerItem = (label: string, value: string) => (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <span style={{ fontSize: 11, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: 0.3 }}>
        {label}
      </span>
      <span style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {value}
      </span>
    </div>
  );

  return (
    <Modal
      title="Split Study"
      open={visible}
      onCancel={onCancel}
      width={820}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={submitting}
          onClick={handleSubmit}
          disabled={loading || selectedKeys.length === 0 || !newAccNo.trim()}
        >
          Split Study
        </Button>,
      ]}
    >
      {/* Patient / study header */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: 12,
          padding: '12px 16px',
          background: '#fafafa',
          border: '1px solid #f0f0f0',
          borderRadius: 6,
          marginBottom: 16,
        }}
      >
        {headerItem('Patient Name', patientHeader.name)}
        {headerItem('Patient Id', patientHeader.id)}
        {headerItem('Accession No', patientHeader.accession)}
        {headerItem('Sex', patientHeader.sex)}
        {headerItem('Scan Date Time', patientHeader.scanDateTime)}
      </div>

      {/* Series-level information */}
      <div style={{ fontSize: 12, fontWeight: 600, color: '#595959', marginBottom: 8 }}>
        SERIES LEVEL INFORMATION
      </div>

      <Spin spinning={loading}>
        {seriesList.length === 0 && !loading ? (
          <Empty description="No series found for this study" />
        ) : (
          <Table
            size="small"
            rowKey="seriesUID"
            columns={columns}
            dataSource={seriesList}
            pagination={false}
            scroll={{ y: 320 }}
            rowSelection={{
              selectedRowKeys: selectedKeys,
              onChange: keys => setSelectedKeys(keys as string[]),
            }}
          />
        )}
      </Spin>

      {/* New accession number */}
      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
          New Accession Number <span style={{ color: '#ff4d4f' }}>*</span>
        </div>
        <Input
          placeholder="Enter a new accession number (different from the original)"
          value={newAccNo}
          onChange={e => setNewAccNo(e.target.value)}
          onPressEnter={handleSubmit}
          maxLength={64}
          allowClear
        />
        <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 6 }}>
          A new order will be created for the selected series with the same patient and study.
        </div>
      </div>
    </Modal>
  );
};

export default SplitStudyModal;
